import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import Holidays from "date-holidays";
import dayjs from "dayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import toast from "react-hot-toast";
import { useUpdateWorkshopMutation } from "../../../app/store/api/workshopApi";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";

const ExceptionalClosures = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const workshopDetails = useSelector(selectWorkshopDetails);
  const [closures, setClosures] = useState(workshopDetails?.closedDates);
  const [newDate, setNewDate] = useState("");
  const [newReason, setNewReason] = useState("");
  const [show, setShow] = useState(false);

  const [updateWorkshop] = useUpdateWorkshopMutation();

  const holidays = useMemo(() => new Holidays("FI"), []);

  const publicHolidays = useMemo(() => {
    const year = dayjs().year();

    const publicHolidayList = holidays
      .getHolidays(year)
      .filter((holiday) => holiday.type === "public")
      .sort((a, b) => a.date.localeCompare(b.date));

    const result = publicHolidayList.map((holiday) => ({
      date: dayjs(holiday.date).format("D.M.YYYY"),
      reason: holiday.name,
    }));

    return result;
  }, [holidays]);

  useEffect(() => {
    setClosures(workshopDetails?.closedDates);
  }, [workshopDetails?.closedDates]);

  const isDuplicate = newDate !== "" && closures.some((c) => c.date === newDate);
  const canAdd = newDate !== "" && !isDuplicate && newReason.trim() !== "";

  const addClosure = () => {
    if (!canAdd) return;
    const payloadClosures = [...closures, { id: crypto.randomUUID(), date: newDate, reason: newReason.trim() }].sort((a, b) => a.date.localeCompare(b.date));
    const payload = { closedDates: payloadClosures };

    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );

    setNewDate("");
    setNewReason("");
  };

  const removeClosure = (id) => {
    const newClosures = closures.filter((c) => c.id !== id).sort((a, b) => a.date.localeCompare(b.date));
    const payload = { closedDates: newClosures };

    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addClosure();
    }
  };

  return (
    <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          onClick={() => setShow(!show)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: "1rem",
                transform: show ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          }
          sx={{
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 700,
            color: "#003C59",
            lineHeight: 1.3,
            p: 0,
            minWidth: 0,
            "&:hover": {
              background: "transparent",
            },
          }}
        >
          {t("workshopExceptionalClosures.publicHolidays")}
        </Button>
      </Box>

      {show && (
        <Stack spacing={1.5} sx={{ mb: 2, transform: show ? "translateY(0)" : "translateY(-10px)", opacity: show ? 1 : 0, transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out" }}>
          {publicHolidays.map((c) => (
            <Stack key={c.id} direction='row' alignItems='center' spacing={2} sx={{ bgcolor: "#f7f8fa", border: "1px solid #e3e6ec", borderRadius: 1.5, px: 2, py: 1.25 }}>
              <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: 1, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonthIcon fontSize='small' sx={{ color: "#8a94a6" }} />
                  <Typography sx={{ minWidth: 100 }}>{c.date}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body2'>{c.reason}</Typography>
                </Box>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
          {t("workshopExceptionalClosures.exceptionalClosures")}
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {closures.map((c) => (
          <Stack key={c.id} direction='row' alignItems='center' spacing={2} sx={{ bgcolor: "#f7f8fa", border: "1px solid #e3e6ec", borderRadius: 1.5, px: 2, py: 1.25 }}>
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: 1, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon fontSize='small' sx={{ color: "#8a94a6" }} />
                <Typography sx={{ minWidth: 100 }}>{dayjs(c.date).format("D.M.YYYY")}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='body2'>{c.reason}</Typography>
              </Box>
            </Box>
            <IconButton size='small' onClick={() => removeClosure(c.id)} aria-label={t("workshopSettings.remove")}>
              <DeleteOutlineIcon fontSize='small' sx={{ color: "#8a94a6" }} />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: "flex-start" }}>
        <TextField
          type='date'
          label={t("workshopExceptionalClosures.datePlaceholder")}
          InputLabelProps={{ shrink: true }}
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          onKeyDown={handleKeyDown}
          error={isDuplicate}
          helperText={isDuplicate ? t("workshopExceptionalClosures.duplicateDate", "This date is already added") : ""}
          sx={{ width: { xs: "100%", sm: 180 } }}
        />
        <TextField placeholder={t("workshopExceptionalClosures.reasonPlaceholder")} value={newReason} onChange={(e) => setNewReason(e.target.value)} onKeyDown={handleKeyDown} fullWidth />
        <Button variant='outlined' startIcon={<AddIcon fontSize='small' />} onClick={addClosure} disabled={!canAdd} sx={{ borderColor: "#e3e6ec", color: "#003C59", height: 55, whiteSpace: "nowrap" }}>
          {t("workshopSettings.add")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default ExceptionalClosures;
